/**
 * React hook for widget management CRUD operations.
 * Used by the admin WidgetManagementPage.
 */

import { useState, useCallback, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'

/** SA-439: a page of the host site the widget's assistant may offer a visitor. */
export interface WidgetDestination {
  key: string
  title: string
  url: string
  purpose: string
}

/** SA-441: `widgets.theme_config` — a SPARSE document (absent key = "use the
 *  stylesheet default"), so every field here is optional. The shape mirrors
 *  `backend/app/services/widget/theme_config.py` (`COLOUR_KEYS` / `FONT_KEYS`
 *  / `PIXEL_KEYS` + `mode` + `advanced`) exactly — keep the two in sync. */
export interface ThemeConfig {
  primary_color?: string
  bg?: string
  bg_secondary?: string
  text?: string
  text_secondary?: string
  border?: string
  assistant_bubble?: string
  assistant_text?: string
  font_family?: string
  heading_font_family?: string
  font_size?: number
  inherit_page_font?: boolean
  border_radius?: number
  control_radius?: number
  bubble_radius?: number
  panel_width?: number
  panel_height?: number
  mode?: 'light' | 'dark' | 'auto'
  advanced?: Record<string, string>
}

export interface WidgetData {
  id: string
  userId: string
  /** SA-361: owner display string, present only when the viewer holds an oversight scope. Never show userId. */
  ownerName: string | null
  defaultPersonaId: string | null
  name: string
  description: string | null
  allowedDomains: string[]
  isActive: boolean
  /** SA-436: the per-widget site rule; origins are stored as typed and normalized by the server.
   *  SA-438: `navigationEnabled` is the "Take me there" opt-in (`site_config.navigation.enabled`).
   *  SA-439: `destinations` are the pages the assistant may offer a visitor. */
  siteConfig: {
    origins: string[]
    rewriteToEmbeddingOrigin: boolean
    navigationEnabled: boolean
    destinations: WidgetDestination[]
  }
  /** SA-440: the per-widget launcher; the server always sends a complete document. */
  launcherConfig: { size: number; offsetX: number; offsetY: number; hidden: boolean }
  /** SA-436: how [Source N] markers render in the widget. */
  sourceMarkers: 'link' | 'strip'
  themeConfig: ThemeConfig
  position: string
  launcherIcon: string | null
  welcomeMessage: Record<string, string>
  greetingBubbles: Record<string, string[]>
  placeholderText: Record<string, string>
  offlineFallbackMessage: Record<string, string>
  autoOpenDelay: number
  showSources: boolean
  showPoweredBy: boolean
  enableVoice: boolean
  enableAvatar: boolean
  enableFileUpload: boolean
  enableNotificationSound: boolean
  defaultLocale: string
  preChatFormEnabled: boolean
  preChatFormFields: any[]
  preChatFormLabels: Record<string, Record<string, string>>
  sessionTimeoutHours: number
  escalationEnabled: boolean
  escalationKeywords: string[]
  escalationMessage: Record<string, string>
  teamHandlingMessage: Record<string, string>
  rateLimitMessages: number
  rateLimitConversations: number
  maxConversationsPerVisitor: number
  maxMessagesPerConversation: number
  maxFileSizeMb: number
  createdAt: string | null
  updatedAt: string | null
}

export interface WidgetAnalyticsData {
  widgetId: string
  period: string
  conversationCount: number
  messageCountUser: number
  messageCountAssistant: number
  uniqueVisitors: number
  avgMessagesPerConversation: number
  newVisitors: number
  returningVisitors: number
}

// snake_case API → camelCase frontend
function transformWidget(raw: any): WidgetData {
  return {
    id: raw.id,
    userId: raw.user_id,
    ownerName: raw.owner_name ?? null,
    defaultPersonaId: raw.default_persona_id,
    name: raw.name,
    description: raw.description,
    allowedDomains: raw.allowed_domains || [],
    isActive: raw.is_active,
    siteConfig: {
      origins: raw.site_config?.origins || [],
      rewriteToEmbeddingOrigin: raw.site_config?.rewrite_to_embedding_origin ?? false,
      navigationEnabled: raw.site_config?.navigation?.enabled ?? false,
      destinations: raw.site_config?.destinations || [],
    },
    launcherConfig: {
      size: raw.launcher_config?.size ?? 56,
      offsetX: raw.launcher_config?.offset_x ?? 20,
      offsetY: raw.launcher_config?.offset_y ?? 20,
      hidden: raw.launcher_config?.hidden ?? false,
    },
    sourceMarkers: raw.source_markers === 'strip' ? 'strip' : 'link',
    themeConfig: raw.theme_config || {},
    position: raw.position || 'bottom-right',
    launcherIcon: raw.launcher_icon,
    welcomeMessage: raw.welcome_message || {},
    greetingBubbles: raw.greeting_bubbles || {},
    placeholderText: raw.placeholder_text || {},
    offlineFallbackMessage: raw.offline_fallback_message || {},
    autoOpenDelay: raw.auto_open_delay || 0,
    showSources: raw.show_sources ?? true,
    showPoweredBy: raw.show_powered_by ?? true,
    enableVoice: raw.enable_voice ?? false,
    enableAvatar: raw.enable_avatar ?? false,
    enableFileUpload: raw.enable_file_upload ?? true,
    enableNotificationSound: raw.enable_notification_sound ?? true,
    defaultLocale: raw.default_locale || 'en',
    preChatFormEnabled: raw.pre_chat_form_enabled ?? false,
    preChatFormFields: raw.pre_chat_form_fields || [],
    preChatFormLabels: raw.pre_chat_form_labels || {},
    sessionTimeoutHours: raw.session_timeout_hours || 24,
    escalationEnabled: raw.escalation_enabled ?? false,
    escalationKeywords: raw.escalation_keywords || [],
    escalationMessage: raw.escalation_message || {},
    teamHandlingMessage: raw.team_handling_message || {},
    rateLimitMessages: raw.rate_limit_messages || 30,
    rateLimitConversations: raw.rate_limit_conversations || 10,
    maxConversationsPerVisitor: raw.max_conversations_per_visitor || 50,
    maxMessagesPerConversation: raw.max_messages_per_conversation || 200,
    maxFileSizeMb: raw.max_file_size_mb || 10,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }
}

function transformAnalytics(raw: any): WidgetAnalyticsData {
  return {
    widgetId: raw.widget_id,
    period: raw.period,
    conversationCount: raw.conversation_count || 0,
    messageCountUser: raw.message_count_user || 0,
    messageCountAssistant: raw.message_count_assistant || 0,
    uniqueVisitors: raw.unique_visitors || 0,
    avgMessagesPerConversation: raw.avg_messages_per_conversation || 0,
    newVisitors: raw.new_visitors || 0,
    returningVisitors: raw.returning_visitors || 0,
  }
}

// camelCase frontend → snake_case API
function serializeWidget(data: Partial<WidgetData>): Record<string, any> {
  const result: Record<string, any> = {}
  const mapping: Record<string, string> = {
    name: 'name',
    description: 'description',
    defaultPersonaId: 'default_persona_id',
    allowedDomains: 'allowed_domains',
    isActive: 'is_active',
    sourceMarkers: 'source_markers',
    themeConfig: 'theme_config',
    position: 'position',
    launcherIcon: 'launcher_icon',
    welcomeMessage: 'welcome_message',
    greetingBubbles: 'greeting_bubbles',
    placeholderText: 'placeholder_text',
    offlineFallbackMessage: 'offline_fallback_message',
    autoOpenDelay: 'auto_open_delay',
    showSources: 'show_sources',
    showPoweredBy: 'show_powered_by',
    enableVoice: 'enable_voice',
    enableAvatar: 'enable_avatar',
    enableFileUpload: 'enable_file_upload',
    enableNotificationSound: 'enable_notification_sound',
    defaultLocale: 'default_locale',
    preChatFormEnabled: 'pre_chat_form_enabled',
    preChatFormFields: 'pre_chat_form_fields',
    preChatFormLabels: 'pre_chat_form_labels',
    sessionTimeoutHours: 'session_timeout_hours',
    escalationEnabled: 'escalation_enabled',
    escalationKeywords: 'escalation_keywords',
    escalationMessage: 'escalation_message',
    teamHandlingMessage: 'team_handling_message',
    rateLimitMessages: 'rate_limit_messages',
    rateLimitConversations: 'rate_limit_conversations',
    maxConversationsPerVisitor: 'max_conversations_per_visitor',
    maxMessagesPerConversation: 'max_messages_per_conversation',
    maxFileSizeMb: 'max_file_size_mb',
  }
  for (const [camel, snake] of Object.entries(mapping)) {
    if (camel in data) {
      result[snake] = (data as any)[camel]
    }
  }
  if ('siteConfig' in data && data.siteConfig) {
    result.site_config = {
      origins: data.siteConfig.origins,
      rewrite_to_embedding_origin: data.siteConfig.rewriteToEmbeddingOrigin,
      navigation: { enabled: data.siteConfig.navigationEnabled },
      // Review fix 7: an all-blank row (e.g. a freshly added, untouched
      // "Add destination" row) is dropped rather than sent — matching the
      // origins textarea's own `.filter(Boolean)` behaviour above. A row
      // with ANY field filled still goes to the server so it gets its own
      // specific validation refusal; silently dropping half-typed work
      // would be worse than a 400.
      destinations: (data.siteConfig.destinations || []).filter((d) =>
        [d.key, d.title, d.url, d.purpose].some((v) => (v ?? '').trim() !== '')
      ),
    }
  }
  if ('launcherConfig' in data && data.launcherConfig) {
    result.launcher_config = {
      size: data.launcherConfig.size,
      offset_x: data.launcherConfig.offsetX,
      offset_y: data.launcherConfig.offsetY,
      hidden: data.launcherConfig.hidden,
    }
  }
  return result
}

export function useWidgets() {
  const [widgets, setWidgets] = useState<WidgetData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchWidgets = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await apiClient.fetch('/api/admin/widgets')
      if (!res.ok) throw new Error('Failed to load widgets')
      const data = await res.json()
      setWidgets((data.widgets || []).map(transformWidget))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createWidget = useCallback(async (data: {
    name: string
    defaultPersonaId: string
    allowedDomains: string[]
    defaultLocale: string
  }) => {
    const res = await apiClient.fetch('/api/admin/widgets', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        default_persona_id: data.defaultPersonaId,
        allowed_domains: data.allowedDomains,
        default_locale: data.defaultLocale,
      }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.detail || 'Failed to create widget')
    }
    const raw = await res.json()
    const widget = transformWidget(raw)
    setWidgets(prev => [widget, ...prev])
    return widget
  }, [])

  const updateWidget = useCallback(async (id: string, data: Partial<WidgetData>) => {
    const body = serializeWidget(data)
    const res = await apiClient.fetch(`/api/admin/widgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const resBody = await res.json().catch(() => ({}))
      throw new Error(resBody.detail || 'Failed to update widget')
    }
    const raw = await res.json()
    const widget = transformWidget(raw)
    setWidgets(prev => prev.map(w => w.id === id ? widget : w))
    return widget
  }, [])

  const deleteWidget = useCallback(async (id: string) => {
    const res = await apiClient.fetch(`/api/admin/widgets/${id}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new Error('Failed to delete widget')
    setWidgets(prev => prev.filter(w => w.id !== id))
  }, [])

  const rotateSecret = useCallback(async (id: string) => {
    const res = await apiClient.fetch(`/api/admin/widgets/${id}/rotate-secret`, {
      method: 'POST',
    })
    if (!res.ok) throw new Error('Failed to rotate secret')
    return res.json()
  }, [])

  const fetchAnalytics = useCallback(async (id: string, period = '7d'): Promise<WidgetAnalyticsData> => {
    const res = await apiClient.fetch(`/api/admin/widgets/${id}/analytics?period=${period}`)
    if (!res.ok) throw new Error('Failed to load analytics')
    const raw = await res.json()
    return transformAnalytics(raw)
  }, [])

  useEffect(() => { fetchWidgets() }, [fetchWidgets])

  return {
    widgets,
    isLoading,
    error,
    fetchWidgets,
    createWidget,
    updateWidget,
    deleteWidget,
    rotateSecret,
    fetchAnalytics,
  }
}
